import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Sparkles } from 'lucide-react';
import SearchSuggestions from './SearchSuggestions';
import { Button, FormGroup } from '../ui';

const SearchBar = ({
  searchQuery,
  setSearchQuery,
  props,
  onShowWhatModal,
  onShowWordCloud,
  onShowColorPicker,
  onShowTimeline,
  selectedFilters,
  setSelectedFilters
}) => {
  const [searchFocused, setSearchFocused] = useState(false);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  const handleSuggestionClick = (suggestion, type) => {
    if (type === 'categories') {
      setSelectedFilters({...selectedFilters, category: suggestion});
    } else {
      setSearchQuery(suggestion);
    }
    setShowSearchSuggestions(false);
    setSearchFocused(false);
  };

  // Handle click outside for suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && !searchRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target)
      ) {
        setShowSearchSuggestions(false);
        setSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-4 mb-6">
      {/* Search Bar - Full Width Row */}
      <div className="relative" ref={searchRef}>
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <FormGroup.Input
          type="text"
          placeholder="Search props, tags, categories, materials..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            setSearchFocused(true);
            setShowSearchSuggestions(true);
          }}
          className={`w-full pl-10 pr-12 py-3 border-2 transition-all duration-200 ${
            searchFocused 
              ? 'border-blue-500 ring-2 ring-blue-200 shadow-lg' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
        />
        
        {/* Clear Search Button */}
        {searchQuery && (
          <Button
            onClick={() => {
              setSearchQuery('');
              searchRef.current?.querySelector('input')?.focus();
            }}
            variant="secondary"
            size="compact"
            icon={X}
            className="absolute right-3 top-3 h-6 w-6 p-0 rounded-full"
          />
        )}
        
        {/* Search Suggestions Dropdown */}
        {showSearchSuggestions && (
          <div ref={suggestionsRef}>
            <SearchSuggestions
              isOpen={showSearchSuggestions}
              props={props}
              onSuggestionClick={handleSuggestionClick}
            />
          </div>
        )}
      </div>
      
      {/* Discovery Tools Row */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={onShowWhatModal}
          variant="gradientPurple"
          size="compact"
          icon={Sparkles}
          className="whitespace-nowrap"
        >
          <span className="hidden sm:inline">What are you looking for?</span>
          <span className="sm:hidden">Find</span>
        </Button>
        <Button
          onClick={onShowWordCloud}
          variant="gradientBlue"
          size="compact"
          className="whitespace-nowrap"
        >
          <span className="text-sm mr-1">☁️</span>
          <span className="hidden sm:inline">Word Cloud</span>
          <span className="sm:hidden">Cloud</span>
        </Button>
        <Button
          onClick={onShowColorPicker}
          variant="gradientPink"
          size="compact"
          className="whitespace-nowrap"
        >
          <span className="text-sm mr-1">🎨</span>
          <span className="hidden sm:inline">Color Picker</span>
          <span className="sm:hidden">Colors</span>
        </Button>
        <Button
          onClick={onShowTimeline}
          variant="gradientGreen"
          size="compact"
          className="whitespace-nowrap"
        >
          <span className="text-sm mr-1">📅</span>
          <span className="hidden sm:inline">Era Timeline</span>
          <span className="sm:hidden">Timeline</span>
        </Button>
      </div>
    </div>
  );
};

export default SearchBar;